import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  Sparkles,
  Camera,
  ImagePlus,
  X,
  LineChart,
} from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SchoolLevel } from '@jeraisonne/db';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import { useAdminChapters } from '@/lib/admin/queries';
import { supabase } from '@/lib/supabase';
import { useBuildBlueprint, type BlueprintSourceKind } from '@/lib/blueprint/api';
import {
  useExerciseSubmission,
  useSubmissionPhotoUrl,
  type ExerciseSubmissionPhoto,
} from '@/lib/exercise-submissions/api';
import {
  extractFromImage,
  fileToBase64,
  validateImageFile,
  joinExtractedStatements,
  extractErrorMessage,
  ExtractError,
  type AcceptedMediaType,
} from '@/lib/exercises/extract-from-image';

/** Types de visuel pris en charge par le Blueprint v1 — le reste (géométrie, vecteurs…) est ignoré au scan. */
const SUPPORTED_VISUAL_TYPES = new Set(['table', 'function_graph']);

type ImportTarget = 'statement' | 'corrige';

/**
 * Route tuteur `/tutor/blueprints/new` — création d'un générateur
 * paramétrique à partir d'un exercice réel (énoncé + corrigé). Le corrigé
 * peut avoir été rédigé par un prof OU un élève — dans les deux cas il reste
 * la SOURCE DE VÉRITÉ du raisonnement, segmentée telle quelle (pas de
 * reconstruction backward, indépendant du système DAG).
 *
 * Deux façons d'y arriver avec une photo :
 *  - `?submission=<id>` : depuis la boîte de réception (/tutor/submissions),
 *    un élève a envoyé 1 à 3 photos — elles s'affichent en haut, prêtes à
 *    importer dans énoncé/corrigé via OCR.
 *  - Import manuel : le prof scanne lui-même un exercice (même OCR,
 *    indépendant de toute soumission).
 * Les deux réutilisent `extract-exercise-image` (déjà utilisé par l'assistant
 * V2 tuteur) — aucune extraction vision directe pour le Blueprint (MVP).
 */
export function TutorBlueprintsNewPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/tutor/blueprints/new' });
  const chapters = useAdminChapters();
  const build = useBuildBlueprint();
  const submission = useExerciseSubmission(search.submission);

  const [title, setTitle] = useState('');
  const [level, setLevel] = useState<SchoolLevel | ''>('');
  const [chapterId, setChapterId] = useState('');
  const [sourceKind, setSourceKind] = useState<BlueprintSourceKind>('teacher');
  const [statement, setStatement] = useState('');
  const [corrige, setCorrige] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [importingKey, setImportingKey] = useState<string | null>(null);
  const [sourceVisual, setSourceVisual] = useState<ExerciseVisualBlock | null>(null);

  // Une soumission élève rédige rarement le corrigé → source_kind='student'.
  useEffect(() => {
    if (submission.data) setSourceKind('student');
  }, [submission.data]);

  const filteredChapters = useMemo(
    () => (chapters.data ?? []).filter((c) => !level || c.level === level),
    [chapters.data, level],
  );

  function handleLevelChange(value: string) {
    setLevel(value as SchoolLevel | '');
    setChapterId('');
  }

  function appendText(target: ImportTarget, text: string) {
    if (target === 'statement') setStatement((prev) => joinExtractedStatements([prev, text]));
    else setCorrige((prev) => joinExtractedStatements([prev, text]));
  }

  async function importFromBase64(
    key: string,
    base64: string,
    mediaType: AcceptedMediaType,
    target: ImportTarget,
  ) {
    setError(null);
    setImportingKey(key);
    try {
      // Le visuel (graphique/tableau) n'est détecté QUE sur le scan de l'énoncé
      // — jamais le corrigé, même règle que les templates : un visuel ne doit
      // jamais référencer un résultat calculé, donc n'a de sens que côté donnée
      // de l'énoncé. Détection en parallèle du texte, comme ExerciseV2Wizard.
      if (target === 'statement') {
        const [textRes, visualRes] = await Promise.allSettled([
          extractFromImage({ image_base64: base64, media_type: mediaType }),
          supabase.functions.invoke<{ visual_blocks?: ExerciseVisualBlock[] }>(
            'extract-visual-blocks',
            {
              body: { image_base64: base64, media_type: mediaType },
            },
          ),
        ]);
        if (textRes.status === 'rejected') throw textRes.reason;
        appendText(target, textRes.value.extracted_text);

        if (!sourceVisual && visualRes.status === 'fulfilled') {
          const detected = Array.isArray(visualRes.value.data?.visual_blocks)
            ? visualRes.value.data!.visual_blocks!
            : [];
          const kept = detected.find((v) => SUPPORTED_VISUAL_TYPES.has(v.type));
          if (kept) setSourceVisual(kept);
        }
      } else {
        const result = await extractFromImage({ image_base64: base64, media_type: mediaType });
        appendText(target, result.extracted_text);
      }
    } catch (e) {
      setError(e instanceof ExtractError ? extractErrorMessage(e.code) : 'Erreur d’extraction');
    } finally {
      setImportingKey(null);
    }
  }

  async function importFromSubmissionPhoto(photo: ExerciseSubmissionPhoto, target: ImportTarget) {
    setError(null);
    const key = `sub-${photo.id}-${target}`;
    setImportingKey(key);
    try {
      const { data: signed, error: signErr } = await supabase.storage
        .from('exercise-submissions')
        .createSignedUrl(photo.storage_path, 300);
      if (signErr || !signed) throw new Error(signErr?.message ?? 'URL signée indisponible');
      const blob = await fetch(signed.signedUrl).then((r) => r.blob());
      const base64 = await fileToBase64(blob);
      const mediaType = (blob.type || 'image/jpeg') as AcceptedMediaType;
      await importFromBase64(key, base64, mediaType, target);
    } catch (e) {
      setImportingKey(null);
      setError(e instanceof Error ? e.message : 'Erreur d’extraction');
    }
  }

  async function importFromFile(file: File, target: ImportTarget) {
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    const key = `manual-${target}`;
    setImportingKey(key);
    try {
      const base64 = await fileToBase64(file);
      await importFromBase64(key, base64, file.type as AcceptedMediaType, target);
    } catch (e) {
      setImportingKey(null);
      setError(e instanceof Error ? e.message : 'Erreur d’extraction');
    }
  }

  const canSubmit =
    !build.isPending &&
    title.trim().length > 0 &&
    !!level &&
    !!chapterId &&
    statement.trim().length >= 10 &&
    corrige.trim().length >= 10;

  async function handleSubmit() {
    setError(null);
    try {
      const result = await build.mutateAsync({
        title: title.trim(),
        level: level as SchoolLevel,
        chapter_id: chapterId,
        statement: statement.trim(),
        corrige: corrige.trim(),
        source_kind: sourceKind,
        source_visual: sourceVisual ?? undefined,
      });
      navigate({
        to: '/tutor/blueprints/$blueprintId',
        params: { blueprintId: result.blueprint_id },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <div className="mb-4 flex items-center gap-2">
        <Link
          to="/tutor/blueprints"
          className="text-ink-500 hover:text-ink-700 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center"
          aria-label="Retour"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
          Nouveau générateur
        </h1>
      </div>

      {submission.data && (
        <div className="border-primary-200 bg-primary-50/40 mb-4 space-y-2 rounded-2xl border p-4">
          <p className="text-primary-700 text-sm font-semibold">
            Exercice envoyé par {submission.data.student_name}
          </p>
          {submission.data.note.trim() && (
            <p className="text-ink-600 text-sm italic">« {submission.data.note} »</p>
          )}
          <p className="text-ink-500 text-xs">
            Touche une photo ci-dessous pour l&apos;importer dans l&apos;énoncé ou le corrigé.
          </p>
          <div className="flex flex-wrap gap-3">
            {submission.data.photos.map((photo) => (
              <SubmissionPhotoImportCard
                key={photo.id}
                photo={photo}
                importingKey={importingKey}
                onImport={(target) => importFromSubmissionPhoto(photo, target)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="border-ink-200 space-y-4 rounded-2xl border bg-white p-4 sm:p-5">
        <Field label="Titre">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Aire d'un rectangle"
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm"
          />
        </Field>

        <Field label="Niveau">
          <select
            value={level}
            onChange={(e) => handleLevelChange(e.target.value)}
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm"
          >
            <option value="">— Choisir un niveau —</option>
            {SCHOOL_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Chapitre">
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            disabled={!level}
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm disabled:opacity-50"
          >
            <option value="">
              {!level
                ? '— Choisis d’abord un niveau —'
                : filteredChapters.length === 0
                  ? `— Aucun chapitre en ${level} —`
                  : '— Choisir un chapitre —'}
            </option>
            {filteredChapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} · {c.subject}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Corrigé rédigé par">
          <div className="flex flex-col gap-2 sm:flex-row">
            {(['teacher', 'student'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setSourceKind(kind)}
                className={`min-h-[2.75rem] flex-1 rounded-xl border px-3 text-sm font-semibold active:scale-[0.98] ${
                  sourceKind === kind
                    ? 'border-primary-400 bg-primary-50 text-primary-700'
                    : 'border-ink-200 text-ink-600 bg-white'
                }`}
              >
                {kind === 'teacher' ? 'Un professeur' : 'Un élève'}
              </button>
            ))}
          </div>
        </Field>

        <Field
          label="Énoncé de l'exercice réel"
          importKey="manual-statement"
          importingKey={importingKey}
          onImportFile={(file) => importFromFile(file, 'statement')}
        >
          <textarea
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            rows={4}
            placeholder="Un rectangle mesure 12 cm de longueur et 7 cm de largeur. Calculer son aire."
            className="border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm"
          />
        </Field>

        {sourceVisual && (
          <div className="border-primary-200 bg-primary-50/40 text-primary-700 flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold">
            <LineChart className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">
              1 visuel détecté ({sourceVisual.type === 'table' ? 'tableau' : 'graphique'}) — sera
              reproduit et adapté à chaque instance générée
            </span>
            <button
              type="button"
              onClick={() => setSourceVisual(null)}
              aria-label="Retirer le visuel détecté"
              className="text-primary-500 hover:text-primary-700 flex min-h-[2.75rem] min-w-[2.75rem] shrink-0 items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <Field
          label="Corrigé (source de vérité — sera segmenté, pas recalculé)"
          importKey="manual-corrige"
          importingKey={importingKey}
          onImportFile={(file) => importFromFile(file, 'corrige')}
        >
          <textarea
            value={corrige}
            onChange={(e) => setCorrige(e.target.value)}
            rows={6}
            placeholder="A = longueur × largeur = 12 × 7 = 84 cm²."
            className="border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm"
          />
        </Field>

        {error && (
          <div className="bg-error-50 border-error-100 text-error-700 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button onClick={handleSubmit} disabled={!canSubmit} fullWidth>
          {build.isPending ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Structuration…
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" /> Construire le générateur
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  importKey,
  importingKey,
  onImportFile,
}: {
  label: string;
  children: React.ReactNode;
  /** Présent uniquement sur les champs énoncé/corrigé : active le bouton "scanner". */
  importKey?: string;
  importingKey?: string | null;
  onImportFile?: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isImporting = !!importKey && importingKey === importKey;

  return (
    <label className="block">
      <span className="mb-1 flex items-center justify-between">
        <span className="text-ink-600 block text-xs font-semibold uppercase tracking-wide">
          {label}
        </span>
        {onImportFile && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={isImporting}
              className="text-primary-600 flex min-h-[2.75rem] items-center gap-1 text-xs font-semibold disabled:opacity-50"
            >
              {isImporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
              Scanner une photo
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              aria-label={`Scanner une photo pour ${label}`}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onImportFile(file);
              }}
            />
          </>
        )}
      </span>
      {children}
    </label>
  );
}

function SubmissionPhotoImportCard({
  photo,
  importingKey,
  onImport,
}: {
  photo: ExerciseSubmissionPhoto;
  importingKey: string | null;
  onImport: (target: ImportTarget) => void;
}) {
  const { data: url } = useSubmissionPhotoUrl(photo.storage_path);
  const busyStatement = importingKey === `sub-${photo.id}-statement`;
  const busyCorrige = importingKey === `sub-${photo.id}-corrige`;

  return (
    <div className="w-28 space-y-1.5">
      <div className="border-ink-200 bg-ink-50 h-28 w-28 overflow-hidden rounded-xl border">
        {url ? (
          <img src={url} alt="Exercice envoyé" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full animate-pulse" />
        )}
      </div>
      <button
        type="button"
        onClick={() => onImport('statement')}
        disabled={busyStatement || busyCorrige}
        className="border-ink-200 text-ink-700 flex min-h-[2.75rem] w-full items-center justify-center gap-1 rounded-lg border bg-white text-[11px] font-semibold disabled:opacity-50"
      >
        {busyStatement ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <ImagePlus className="h-3 w-3" />
        )}
        → Énoncé
      </button>
      <button
        type="button"
        onClick={() => onImport('corrige')}
        disabled={busyStatement || busyCorrige}
        className="border-ink-200 text-ink-700 flex min-h-[2.75rem] w-full items-center justify-center gap-1 rounded-lg border bg-white text-[11px] font-semibold disabled:opacity-50"
      >
        {busyCorrige ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <ImagePlus className="h-3 w-3" />
        )}
        → Corrigé
      </button>
    </div>
  );
}
